import React, { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Grid } from "@mui/material";
import PropTypes from "prop-types";
import * as Yup from "yup";
import Autocomplete from "@mui/material/Autocomplete";
import MuiTextField from "@mui/material/TextField";
import { DividerStyled, FormLabelStyled, GridItemStyled, TextFieldStyled } from "./CreateEmployerForm.styles";
import { TranslationKeys } from "../../../../translations";
import { employerService } from "../../Services/EmployerService";
const CreateEmployerForm = (props) => {
  const {
    employer,
    owners,
    setEmployersHolding,
    setSubOwners,
    subOwners,
    setSelectedOwner,
    selectedOwner,
    setSelectedParent,
    setEmployer
  } = props;
  const intl = useIntl();
  const [employerErrorField, setEmployerErrorField] = useState(null);
  const [employerErrorMessage, setEmployerErrorMessage] = useState(null);
  const [subOwner, setSubOwner] = useState(null);
  const validationSchema = Yup.object().shape({
    employerName: Yup.string().trim().strict().max(255).required(),
    employerEmail: Yup.string().trim().strict().max(255).email(),
    employerPhone: Yup.string().trim().strict().max(255),
    internalNumber: Yup.string().trim().strict().max(255).test(
      "internalNumber",
      intl.formatMessage({ id: TranslationKeys.validationMessage_internalNumber_alreadyExists }),
      async (value) => {
        if (!value) return true;
        try {
          await employerService.validateField("internalNumber", value);
        } catch (error) {
          if (error.response?.status === 422) {
            return false;
          }
          throw error;
        }
        return true;
      }
    ),
    contactName: Yup.string().trim().strict().max(255),
    contactEmail: Yup.string().trim().strict().max(255).email(),
    contactPhone: Yup.string().trim().strict().max(255),
    chamberOfCommerce: Yup.string().trim().strict().max(255),
    sectorCode: Yup.string().trim().strict().max(50),
    payrollTaxNumber: Yup.string().trim().strict().max(255),
    address: Yup.string().trim().strict().required(),
    zipCode: Yup.string().trim().strict().max(255).required(),
    city: Yup.string().trim().strict().max(255).required()
  });
  const handleChange = (field) => (event) => {
    employer[field] = event.target.value;
    Yup.reach(validationSchema, field).validate(employer[field]).then(() => {
      setEmployerErrorField(null);
      setEmployerErrorMessage(null);
    }).catch((e) => {
      setEmployerErrorField(field);
      setEmployerErrorMessage(e.message);
    });
    props.onEmployerChange(field, event.target.value);
  };
  const ownerAutoComplete = useMemo(() => {
    return /* @__PURE__ */ React.createElement(
      Autocomplete,
      {
        name: "ownerId",
        options: owners,
        getOptionLabel: (option) => {
          return option.ownerName;
        },
        disableClearable: true,
        isOptionEqualToValue: (item, current) => {
          if (typeof current === "string") {
            return item.value === current;
          }
          const currentValue = current.value ? current.value : current.ownerId;
          return item.ownerId === currentValue;
        },
        onChange: (e, value) => {
          setSelectedParent(null);
          setSubOwner(null);
          setSelectedOwner(value);
          setEmployersHolding(value.employers ?? []);
          setSubOwners(value.subOwners ?? []);
          setEmployer({
            ...employer,
            ownerId: value.ownerId,
            parentId: null
          });
        },
        renderInput: (params) => /* @__PURE__ */ React.createElement(MuiTextField, { ...params, name: "ownerId", variant: "outlined", fullWidth: true })
      }
    );
  }, [employer, owners, setEmployer, setEmployersHolding, setSelectedOwner, setSelectedParent, setSubOwners]);
  const subOwnersAutoComplete = useMemo(() => {
    return /* @__PURE__ */ React.createElement(
      Autocomplete,
      {
        name: "subOwnerId",
        options: subOwners,
        getOptionLabel: (option) => {
          return option.ownerName;
        },
        disableClearable: false,
        isOptionEqualToValue: (item, current) => {
          if (typeof current === "string") {
            return item.value === current;
          }
          const currentValue = current.value ? current.value : current.ownerId;
          return item.ownerId === currentValue;
        },
        value: subOwner,
        onChange: (e, value) => {
          setSelectedParent(null);
          if (!value) {
            setSubOwner(null);
            setEmployersHolding(selectedOwner?.employers ?? []);
            setEmployer({
              ...employer,
              ownerId: selectedOwner.ownerId,
              parentId: null
            });
          } else {
            setSubOwner(subOwners.find((item) => item.ownerId === value.ownerId));
            setEmployersHolding(value.employers ?? []);
            setEmployer({
              ...employer,
              ownerId: value.ownerId,
              parentId: null
            });
          }
        },
        renderInput: (params) => /* @__PURE__ */ React.createElement(MuiTextField, { ...params, name: "subOwnerId", variant: "outlined", fullWidth: true })
      }
    );
  }, [employer, subOwner, selectedOwner, setEmployer, setEmployersHolding, setSelectedParent, subOwners]);
  return /* @__PURE__ */ React.createElement(Grid, { sx: { width: "100%" }, container: true, spacing: 2 }, /* @__PURE__ */ React.createElement(GridItemStyled, { item: true, xs: 6 }, /* @__PURE__ */ React.createElement(FormLabelStyled, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.name" }), " *"), /* @__PURE__ */ React.createElement(
    TextFieldStyled,
    {
      color: "secondary",
      error: employerErrorField === "employerName",
      fullWidth: true,
      helperText: employerErrorField === "employerName" ? employerErrorMessage : null,
      name: "employer.employerName",
      placeholder: intl.formatMessage({ id: "employers.name" }).toUpperCase(),
      variant: "outlined",
      onChange: handleChange("employerName")
    }
  )), /* @__PURE__ */ React.createElement(GridItemStyled, { item: true, xs: 6 }, /* @__PURE__ */ React.createElement(FormLabelStyled, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.chamberOfCommerce" })), /* @__PURE__ */ React.createElement(
    TextFieldStyled,
    {
      color: "secondary",
      fullWidth: true,
      name: "chamberOfCommerce",
      placeholder: intl.formatMessage({ id: "employers.chamberOfCommerce" }).toUpperCase(),
      variant: "outlined",
      onChange: handleChange("chamberOfCommerce")
    }
  )), /* @__PURE__ */ React.createElement(GridItemStyled, { item: true, xs: 6 }, /* @__PURE__ */ React.createElement(FormLabelStyled, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.owner" }), " *"), ownerAutoComplete), /* @__PURE__ */ React.createElement(GridItemStyled, { item: true, xs: 6 }, /* @__PURE__ */ React.createElement(FormLabelStyled, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.subOwner" })), subOwnersAutoComplete), /* @__PURE__ */ React.createElement(GridItemStyled, { item: true, xs: 6 }, /* @__PURE__ */ React.createElement(FormLabelStyled, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.sectorCode" })), /* @__PURE__ */ React.createElement(
    TextFieldStyled,
    {
      color: "secondary",
      fullWidth: true,
      name: "sectorCode",
      placeholder: intl.formatMessage({ id: "employers.sectorCode" }).toUpperCase(),
      variant: "outlined",
      onChange: handleChange("sectorCode")
    }
  )), /* @__PURE__ */ React.createElement(GridItemStyled, { item: true, xs: 6 }, /* @__PURE__ */ React.createElement(FormLabelStyled, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.payrollTaxNumber" })), /* @__PURE__ */ React.createElement(
    TextFieldStyled,
    {
      color: "secondary",
      fullWidth: true,
      name: "payrollTaxNumber",
      placeholder: intl.formatMessage({ id: "employers.payrollTaxNumber" }).toUpperCase(),
      variant: "outlined",
      onChange: handleChange("payrollTaxNumber")
    }
  )), /* @__PURE__ */ React.createElement(GridItemStyled, { item: true, xs: 6 }, /* @__PURE__ */ React.createElement(FormLabelStyled, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.address" }), " *"), /* @__PURE__ */ React.createElement(
    TextFieldStyled,
    {
      color: "secondary",
      fullWidth: true,
      name: "address",
      placeholder: intl.formatMessage({ id: "employers.address" }).toUpperCase(),
      variant: "outlined",
      onChange: handleChange("address"),
      error: employerErrorField === "address",
      helperText: employerErrorField === "address" ? employerErrorMessage : null
    }
  )), /* @__PURE__ */ React.createElement(GridItemStyled, { item: true, xs: 6 }, /* @__PURE__ */ React.createElement(FormLabelStyled, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.zipCode" }), " *"), /* @__PURE__ */ React.createElement(
    TextFieldStyled,
    {
      color: "secondary",
      fullWidth: true,
      name: "zipCode",
      placeholder: intl.formatMessage({ id: "employers.zipCode" }).toUpperCase(),
      variant: "outlined",
      onChange: handleChange("zipCode"),
      error: employerErrorField === "zipCode",
      helperText: employerErrorField === "zipCode" ? employerErrorMessage : null
    }
  )), /* @__PURE__ */ React.createElement(GridItemStyled, { item: true, xs: 6 }, /* @__PURE__ */ React.createElement(FormLabelStyled, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.city" }), " *"), /* @__PURE__ */ React.createElement(
    TextFieldStyled,
    {
      color: "secondary",
      fullWidth: true,
      name: "city",
      placeholder: intl.formatMessage({ id: "employers.city" }).toUpperCase(),
      variant: "outlined",
      onChange: handleChange("city"),
      error: employerErrorField === "city",
      helperText: employerErrorField === "city" ? employerErrorMessage : null
    }
  )), /* @__PURE__ */ React.createElement(GridItemStyled, { item: true, xs: 6, style: { display: "none" } }, /* @__PURE__ */ React.createElement(FormLabelStyled, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.numberOfEmployees" })), /* @__PURE__ */ React.createElement(
    TextFieldStyled,
    {
      small: true,
      color: "secondary",
      disabled: true,
      name: "numberOfEmployees",
      placeholder: intl.formatMessage({ id: "employers.numberOfEmployees" }).toUpperCase(),
      type: "number",
      variant: "outlined",
      onChange: handleChange("numberOfEmployees")
    }
  )), /* @__PURE__ */ React.createElement(GridItemStyled, { item: true, xs: 6 }, /* @__PURE__ */ React.createElement(FormLabelStyled, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.employerEmail" })), /* @__PURE__ */ React.createElement(
    TextFieldStyled,
    {
      color: "secondary",
      error: employerErrorField === "employerEmail",
      fullWidth: true,
      helperText: employerErrorField === "employerEmail" ? employerErrorMessage : null,
      name: "employerEmail",
      placeholder: intl.formatMessage({ id: "employers.email" }).toUpperCase(),
      required: true,
      type: "email",
      variant: "outlined",
      onChange: handleChange("employerEmail")
    }
  )), /* @__PURE__ */ React.createElement(GridItemStyled, { item: true, xs: 6 }, /* @__PURE__ */ React.createElement(FormLabelStyled, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.employerPhone" })), /* @__PURE__ */ React.createElement(
    TextFieldStyled,
    {
      color: "secondary",
      fullWidth: true,
      name: "employerPhone",
      placeholder: intl.formatMessage({ id: "employers.employerPhone" }).toUpperCase(),
      variant: "outlined",
      onChange: handleChange("employerPhone")
    }
  )), /* @__PURE__ */ React.createElement(GridItemStyled, { item: true, xs: 6 }, /* @__PURE__ */ React.createElement(FormLabelStyled, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.internalNumber" })), /* @__PURE__ */ React.createElement(
    TextFieldStyled,
    {
      color: "secondary",
      fullWidth: true,
      name: "employerPhone",
      placeholder: intl.formatMessage({ id: "employers.internalNumber" }).toUpperCase(),
      variant: "outlined",
      onChange: handleChange("internalNumber"),
      error: employerErrorField === "internalNumber",
      helperText: employerErrorField === "internalNumber" ? employerErrorMessage : null
    }
  )), /* @__PURE__ */ React.createElement(DividerStyled, null), /* @__PURE__ */ React.createElement(GridItemStyled, { item: true, xs: 6 }, /* @__PURE__ */ React.createElement(FormLabelStyled, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.contactName" })), /* @__PURE__ */ React.createElement(
    TextFieldStyled,
    {
      color: "secondary",
      fullWidth: true,
      name: "contactName",
      placeholder: intl.formatMessage({ id: "employers.contactName" }).toUpperCase(),
      variant: "outlined",
      onChange: handleChange("contactName")
    }
  )), /* @__PURE__ */ React.createElement(GridItemStyled, { item: true, xs: 6 }), /* @__PURE__ */ React.createElement(GridItemStyled, { item: true, xs: 6 }, /* @__PURE__ */ React.createElement(FormLabelStyled, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.contactEmail" })), /* @__PURE__ */ React.createElement(
    TextFieldStyled,
    {
      color: "secondary",
      fullWidth: true,
      name: "contactEmail",
      placeholder: intl.formatMessage({ id: "employers.contactEmail" }).toUpperCase(),
      type: "email",
      variant: "outlined",
      onChange: handleChange("contactEmail")
    }
  )), /* @__PURE__ */ React.createElement(GridItemStyled, { item: true, xs: 6 }, /* @__PURE__ */ React.createElement(FormLabelStyled, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employers.contactPhone" })), /* @__PURE__ */ React.createElement(
    TextFieldStyled,
    {
      color: "secondary",
      fullWidth: true,
      name: "contactPhone",
      placeholder: intl.formatMessage({ id: "employers.contactPhone" }).toUpperCase(),
      variant: "outlined",
      onChange: handleChange("contactPhone")
    }
  )));
};
CreateEmployerForm.propTypes = {
  employer: PropTypes.object,
  employerShowHolding: PropTypes.bool,
  employerIsHolding: PropTypes.bool,
  onEmployerChange: PropTypes.func
};
CreateEmployerForm.defaultProps = {
  employerShowHolding: true,
  employerIsHolding: false
};
export default CreateEmployerForm;
